import type { PaymentMethodsResponse } from '@adyen/adyen-web';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { differenceInSeconds, parse, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { flatten, isNumber, sortBy } from 'lodash-es';
import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';
import { v4 as uuidv4 } from 'uuid';
import {
  type DeliverySlot,
  type ETACalculation,
  type ETACost,
  ETAPaymentMethod,
  ETARequests,
  type ETAWarehouse,
  isPhonePaymentMethod,
  MechanicType,
} from '~/api/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import {
  type AwsPayment,
  type FillFeedbackRequest,
  type GiftItem,
  type GiftMechanism,
  type NewOrderCartItemOffer,
  type NewOrderResponse,
  type Order,
  type OrderAddress,
  type OrderPublicStatus,
  OrderRequests,
  type OrderShort,
  type OrderStatus,
  type OrdersResponse,
  type Payment,
  PaymentSystem,
  type PaymentSystemItem,
  type VerifonePaymentCard,
} from '~/api/Order';
import type { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { SECOND } from '~/constants/variables';
import { ACTIVE_ORDER_STATUS_CRITERIA } from '~/pages/Cabinet/constants';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, checkoutStore, mainStore } from '~/stores';
import type { Offer } from '~/stores/CategoriesStore';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type OrderData from '~/stores/CheckoutStore/OrderData';
import { normalizeDeliveryCost } from '~/stores/OrderStore/utils';
import { deleteCookie, setCookie } from '~/utils/cookies';
import { CURRENCY_SYMBOL } from '../constants';
import { DeliveryStore } from '../DeliveryStore';
import { storage as localStorage } from '../LocalStorage';
import type { UserStore } from '../UserStore';
import type { IOrderStore } from './interfaces';
import { handleOrderError, OrderErrorHandled } from './parseOrderError';

export type OrderStatusStore =
  | 'none'
  | 'accepted'
  | 'picking'
  | 'readyToShip'
  | 'readyForPickup'
  | 'inDelivery'
  | 'delivered'
  | 'failed'
  | 'cancelled';

export type RequestETAResponse = {
  oldPrice: string;
  newPrice: string;
  etaCalculation?: Nullable<ETACalculation>;
};

export interface RateInfo {
  rating: number;
  deliveryRating?: number;
  productVarietyRating?: number;
  productQualityRating?: number;
  comment?: string;
}

export interface ExpressDeliveryLabelResult {
  message: string;
  args: Record<string, string>;
  showFlashIcon: boolean;
  expressDelivery?: ETAWarehouse['availability']['deliveryMechanics']['ON_DEMAND'];
  slot?: DeliverySlot;
}

export interface ExpressDeliveryLabelProps {
  timeFormat: string;
  locale: any;
  onlySlot?: boolean;
  onlyTime?: boolean;
}

const DEFAULT_FEES_COST: ETACost = {
  shipping: 0,
  threshold: 0,
  shippingPounds: '0',
  thresholdPounds: '0',
  serviceFee: 0,
  serviceFeeThreshold: 0,
  serviceFeeThresholdPounds: '0',
  serviceFeeShippingPounds: '0',
};

export class OrderStore implements IOrderStore {
  etaCalculation: ETACalculation | null = null;
  etaSlotCalculation: ETACalculation | null = null;
  orderStatus: OrderStatusStore = 'none';
  orderList: OrderShort[] | null = null;
  isFirstOrder: Nullable<boolean> = null;
  currentOrdersPage = 1;
  activeOrderID = '';
  activeOrderBonuses: number | null = null;
  lastRatedOrderID = '';
  activeOrderProductCount = 0;
  paymentCards: Payment[] = [];
  paymentMethods: PaymentMethodsResponse['paymentMethods'] = [];
  activeGift: GiftItem | null = null;
  gift: GiftMechanism | null = null;
  isAuthInCart = false;
  tipAmount = '';
  idempotencyKey = '';
  isRegularTips = false;
  regularTipsValue = '';
  isCalculateTips = false;
  currency = 'aed';
  paymentSystems = new Map<PaymentSystem, ETAPaymentMethod[]>();
  paymentMethodsMap = new Map<ETAPaymentMethod, PaymentSystem[]>();
  isETALoading = false;
  isLoading = false;
  downloadIsPossible = false;
  url3Ds = '';
  onBackFromPayment: (() => Promise<void>) | null = null;

  readonly deliveryStore: DeliveryStore;

  private _ordersPollingEnabled: boolean = false;
  private _ordersPollingTimeoutRef: Nullable<ReturnType<typeof setTimeout>> =
    null;

  constructor(protected readonly userStore: UserStore) {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'OrderStore',
      properties: [
        'etaCalculation',
        'paymentCards',
        'activeGift',
        'gift',
        'lastRatedOrderID',
        'idempotencyKey',
        'regularTipsValue',
        'isRegularTips',
        'currency',
      ],
      storage: localStorage,
    }).catch((error) => error && console.error(error));

    if (
      typeof window !== 'undefined' &&
      window.__INITIAL_STATE__ &&
      window.__INITIAL_STATE__.etaData?.warehouse
    ) {
      this.setEtaCalculation(window.__INITIAL_STATE__.etaData);
    }

    this.deliveryStore = new DeliveryStore(this);

    reaction(
      () => this._ordersPollingEnabled,
      (arg) => {
        if (!arg) {
          this._clearOrdersPollingPayload();
          return;
        }

        this._startOrdersPolling();
      },
    );

    reaction(
      () => this.userStore.personalData.id,
      (id) => {
        if (!id) {
          return;
        }

        this.requestOrders();
      },
    );
  }

  get orderTipsValue(): string {
    if (
      checkoutStore.deliveryMethod === ETADeliveryMethodType.ClickAndCollect
    ) {
      return '';
    }

    if (!this.isCalculateTips) {
      return '';
    }

    return this.regularTipsValue;
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  getWHCode(): string | null {
    return this.etaCalculation?.warehouse.code || null;
  }

  get fee(): ETACost {
    return (
      this.etaSlotCalculation?.cost[checkoutStore.deliveryMethod] ??
      this.etaCalculation?.cost[checkoutStore.deliveryMethod] ??
      DEFAULT_FEES_COST
    );
  }

  get cartPriceForGift(): string {
    if (!this.gift) {
      return '';
    }
    if (!this.gift.minimum_order_amount) {
      return '';
    }
    const diff =
      mainStore.convertPoundsToPence(this.gift.minimum_order_amount) -
      mainStore.convertPoundsToPence(cartStore.finalPrice);
    return diff <= 0 ? '' : mainStore.convertPenceToPounds(diff);
  }

  get etaWarehouseCode(): string {
    if (!this.etaCalculation) {
      return company.config.warehouse.default;
    }
    return (
      (this.userStore.deliveryAddress && this.etaCalculation.warehouse.code) ||
      company.config.warehouse.default
    );
  }

  get isStoreAvailable() {
    return this.etaCalculation?.warehouse.availability.availableNow;
  }

  get whCode(): string {
    return this.etaCalculation?.warehouse.code ?? '';
  }

  get isPickupAvailable(): boolean {
    return !!this.etaCalculation?.warehouse.deliveryMethods.find(
      ({ type }) => type === ETADeliveryMethodType.ClickAndCollect,
    );
  }

  get currencySymbol(): string {
    return CURRENCY_SYMBOL[this.currency] ?? '';
  }

  get isExpressAvailableNow(): boolean {
    return !!this.etaCalculation?.warehouse?.availability?.deliveryMechanics
      ?.ON_DEMAND?.availableNow;
  }

  get finishedOrders() {
    return (
      this.orderList?.filter((i) =>
        ['delivered', 'failed', 'cancelled'].includes(i.public_status),
      ) ?? []
    );
  }

  get activeOrders() {
    return (
      this.orderList?.filter(
        (i) => !['delivered', 'failed', 'cancelled'].includes(i.public_status),
      ) ?? []
    );
  }
  get slotDeliverySchedule() {
    const getSecondsSinceStartOfDay = (date: number) => {
      const start = startOfDay(date);
      return differenceInSeconds(date, start);
    };

    const sortedSlots = flatten(
      sortBy(Object.entries(checkoutStore.deliverySlots), [
        ([time]) => Number(time),
      ])
        .map((entry) => toJS(entry[1]))
        .filter((slots) => Boolean(slots.length)),
    );
    if (sortedSlots.length === 0) {
      return null;
    }
    const { min_time_string, max_time_string } = sortedSlots.reduce(
      (
        acc,
        {
          delivery_min_time,
          delivery_max_time,
          delivery_min_time_string,
          delivery_max_time_string,
        },
      ) => {
        const min_time = getSecondsSinceStartOfDay(delivery_min_time * 1000);
        const max_time = getSecondsSinceStartOfDay(delivery_max_time * 1000);

        if (acc.min_time === Infinity || min_time < acc.min_time) {
          acc.min_time = min_time;
          acc.min_time_string =
            delivery_min_time_string.split(',')[2]?.trim() || '';
        }
        //Use latest time of same day
        if (max_time > acc.max_time) {
          acc.max_time = max_time;
          acc.max_time_string =
            delivery_max_time_string.split(',')[2]?.trim() || '';
        }

        return acc;
      },
      {
        min_time: Infinity,
        max_time: -Infinity,
        min_time_string: '00:00',
        max_time_string: '23:59',
      },
    );

    return {
      opening: min_time_string,
      closing: max_time_string,
    };
  }
  get closestSlot() {
    return sortBy(Object.entries(checkoutStore.deliverySlots), [
      ([time]) => Number(time),
    ])
      .map((entry) => entry[1])
      .find((slots) => Boolean(slots.length))?.[0];
  }

  get warehouseAvailability() {
    return this.etaCalculation?.warehouse.availability;
  }

  get expressDelivery() {
    return this.etaCalculation?.warehouse.availability.deliveryMechanics?.[
      MechanicType.ON_DEMAND
    ];
  }

  get scheduledDelivery() {
    return this.etaCalculation?.warehouse.availability.deliveryMechanics?.[
      MechanicType.SLOT
    ];
  }

  getDeliveryLabel({
    timeFormat,
    locale,
    onlySlot,
    onlyTime,
  }: ExpressDeliveryLabelProps): ExpressDeliveryLabelResult {
    const isPickup =
      checkoutStore.deliveryMethod === ETADeliveryMethodType.ClickAndCollect;
    const defaultMessage = isPickup
      ? 'phrases:pickupBannerTitle'
      : 'phrases:expressDeliveryBannerTitle';
    const defaultArgs = { time: '30' };
    const defaultLabel: ExpressDeliveryLabelResult = {
      message: defaultMessage,
      args: defaultArgs,
      showFlashIcon: !isPickup,
    };

    if (!this.etaCalculation) {
      return defaultLabel;
    }

    const onDemandDeliveryMechanic = this.expressDelivery;

    const isExpressDeliveryAvailable = Boolean(
      onDemandDeliveryMechanic && onDemandDeliveryMechanic.availableNow,
    );

    const closestDeliverySlot = this.closestSlot;

    if (onlySlot || (!isExpressDeliveryAvailable && closestDeliverySlot)) {
      const slot = closestDeliverySlot;

      if (!slot) {
        return defaultLabel;
      }

      const minTimeSlices = slot.delivery_min_time_string.split(', ');
      const parsedDateMin = parse(
        slot.delivery_min_time_string,
        'dd-MM-yyyy, EEEE, HH:mm',
        new Date(),
      );
      const maxTimeSlices = slot.delivery_max_time_string.split(', ');
      const parsedDateMax = parse(
        slot.delivery_max_time_string,
        'dd-MM-yyyy, EEEE, HH:mm',
        new Date(),
      );

      if (!minTimeSlices.length || !maxTimeSlices.length) {
        return defaultLabel;
      }

      return {
        message: 'phrases:slotDeliveryBannerTitle',
        args: {
          time: `${format(
            parsedDateMin,
            onlyTime ? timeFormat : `EEEE ${timeFormat}`,
            {
              timeZone: company.config.timeZone,
              locale,
            },
          )}\u00a0-\u00a0${format(parsedDateMax, timeFormat, {
            timeZone: company.config.timeZone,
            locale,
          })}`,
          date: format(parsedDateMin, 'EEEE', {
            timeZone: company.config.timeZone,
            locale,
          }),
        },
        showFlashIcon: false,
        slot,
      };
    }

    const { duration } = this.etaCalculation || {};

    if (!closestDeliverySlot && !duration.min && !duration.max) {
      return defaultLabel;
    }

    return {
      message: defaultMessage,
      args: {
        time: duration.range,
      },
      showFlashIcon: !isPickup,
      expressDelivery: onDemandDeliveryMechanic,
    };
  }

  // Setters
  setIsFirstOrder(flag: boolean) {
    this.isFirstOrder = flag;
  }

  setEtaCalculation(val: ETACalculation | null) {
    this.etaCalculation = val;

    if (typeof window !== 'undefined') {
      val?.warehouse.code && this.userStore.deliveryAddress
        ? setCookie('warehouse', val.warehouse.code, { 'max-age': 2678400 })
        : deleteCookie('warehouse');
    }
  }

  setEtaSlotCalculation(val: ETACalculation | null) {
    this.etaSlotCalculation = val;
  }

  setIsLoading(val: boolean) {
    this.isLoading = val;
  }

  setOrderStatus(val: OrderPublicStatus | 'none') {
    let status: OrderStatusStore;
    switch (val) {
      case 'in_delivery':
        status = 'inDelivery';
        break;
      case 'ready_to_ship':
        status = 'readyToShip';
        break;
      case 'ready_for_pickup':
        status = 'readyForPickup';
        break;
      default:
        status = val;
        break;
    }
    this.orderStatus = status;
    analyticsService.track(AnalyticsEvent.ORDER_STATUS_CHANGED, {
      status: val,
      mapped_status: status,
    });
  }

  setActiveOrderID(val: string) {
    this.activeOrderID = val;
  }

  setActiveOrderBonuses(val: number | null) {
    this.activeOrderBonuses = val;
  }

  setLastRatedOrderID(val: string) {
    this.lastRatedOrderID = val;
  }

  setActiveOrderProductCount(val: number) {
    this.activeOrderProductCount = val;
  }

  setPaymentCards(arr: Payment[]) {
    this.paymentCards = arr;
  }

  setGift(val: GiftMechanism | null) {
    this.gift = val;
  }

  setActiveGift(val: GiftItem | null) {
    this.activeGift = val;
  }

  setIsAuthInCart(flag: boolean) {
    this.isAuthInCart = flag;
  }

  setCurrentOrdersPage(val: number) {
    this.currentOrdersPage = val;
  }
  setDownloadIsPossible(val: boolean) {
    this.downloadIsPossible = val;
  }

  setTipAmount(val: string) {
    this.tipAmount = val;
  }

  setCurrency(val: string) {
    this.currency = (val || 'aed').toLowerCase();
  }

  setPaymentSystems(systems: PaymentSystemItem[]) {
    this.paymentSystems = new Map(
      systems.map((item) => [item.system, item.methods]),
    );

    const paymentMethodsMap = new Map<ETAPaymentMethod, PaymentSystem[]>();
    for (const { system, methods } of systems) {
      for (const method of methods) {
        const list = paymentMethodsMap.get(method) ?? [];
        list.push(system);
        paymentMethodsMap.set(method, list);
      }
    }
    this.paymentMethodsMap = paymentMethodsMap;
  }

  setIsETALoading(val: boolean) {
    this.isETALoading = val;
  }

  set3dsUrl(val: string) {
    this.url3Ds = val;
  }

  setOrdersPollingEnabled(val: boolean) {
    this._ordersPollingEnabled = val;
  }

  setOnBackFromPayment(handler: (() => Promise<void>) | null) {
    this.onBackFromPayment = handler;
  }

  // Actions
  getOrderById(id: string): OrderShort | null {
    if (!this.orderList) {
      return null;
    }
    for (let i = 0; i < this.orderList.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (this.orderList[i].id === id) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        return this.orderList[i];
      }
    }
    return null;
  }

  calcOrderItems(order: OrderShort): OrderShort {
    const itemsCount = {
      actual: 0,
      requested: 0,
    };
    for (let i = 0; i < order.items.length; i++) {
      itemsCount.actual +=
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        order.items[i].actual_quantity ?? order.items[i].requested_quantity;
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      itemsCount.requested += order.items[i].requested_quantity;
    }
    order.itemsCount = itemsCount;
    return order;
  }

  // Requests API
  fetchETA(
    pos: string,
    deliveryMethod?: ETADeliveryMethodType,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
    },
  ): Promise<ETACalculation | null> {
    this.setIsETALoading(true);

    return this.deliveryStore
      .requestActualVersionETA(pos, deliveryMethod, opts)
      .then((etaResponse) => {
        runInAction(() => {
          mainStore.sendToRN('sendTags', {
            warehouse_code: company.useDefaultWarehouseWhenMissing
              ? (etaResponse.warehouse.code ?? company.config.warehouse.default)
              : etaResponse.warehouse.code,
          });
        });

        return etaResponse;
      })
      .finally(() => {
        this.setIsETALoading(false);
      });
  }

  async updateOrdersAnalytics() {
    if (!this.userStore.personalData.id) {
      return;
    }
    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
      });
      let successfulOrders: OrdersResponse;
      if (total_count) {
        successfulOrders = await OrderRequests.getOrders({
          filter: {
            customer_id: this.userStore.personalData.id,
            status: [
              'created',
              'reserved',
              'confirmed',
              'delivery_created',
              'wh_created',
              'picking',
              'ready_to_ship',
              'in_delivery',
              'delivered',
              'delivered_partially',
              'finished',
            ],
          },
        });
      }
      runInAction(() => {
        mainStore.analytics.totalSpent = mainStore.convertPenceToPounds(
          (successfulOrders?.orders || []).reduce(
            (sum, order) => sum + (order.paid_total || 0),
            0,
          ),
        );
        mainStore.analytics.totalOrders = total_count;
        mainStore.analytics.totalSuccessfulOrders =
          successfulOrders?.total_count || 0;
        if (orders.length) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          mainStore.analytics.lastOrderCreatedAt = orders[0].created_at;
          mainStore.analytics.firstOrderCreatedAt =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            orders[orders.length - 1].created_at;
        }
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total spent': mainStore.analytics.totalSpent,
        });
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total orders': total_count,
        });
        if (orders.length) {
          mainStore.sendToRN('setUserProperties', {
            'Commerce: first order date':
              mainStore.analytics.firstOrderCreatedAt,
          });
        }
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  etaCalculationResponse(data: ETACalculation) {
    const paymentMethods = data.warehouse.deliveryMethods.reduce(
      (acc, { type, paymentMethods }) => ({ ...acc, [type]: paymentMethods }),
      {} as ETACalculation['paymentMethods'],
    );

    const cost: ETACalculation['cost'] = data.warehouse.deliveryMethods.reduce(
      (acc, { cost, type }) => {
        if (acc[type]) {
          return acc;
        }

        const deliveryMethodCost: ETACost = {
          ...cost,
          shippingPounds: mainStore.convertPenceToPounds(cost.shipping),
          thresholdPounds: mainStore.convertPenceToPounds(cost.threshold),
          serviceFeeThresholdPounds: mainStore.convertPenceToPounds(
            cost.serviceFeeThreshold,
          ),
          serviceFeeShippingPounds: mainStore.convertPenceToPounds(
            cost.serviceFee,
          ),
        };

        return { ...acc, [type]: normalizeDeliveryCost(deliveryMethodCost) };
      },
      {} as ETACalculation['cost'],
    );

    const zone: Record<string, { id: string; name: string }> =
      data.warehouse.deliveryMethods.reduce(
        (acc, { type }) => ({ ...acc, [type]: data.warehouse.zone }),
        {},
      );

    return {
      cost,
      paymentMethods,
      distance: data.distance,
      duration: {
        ...data.duration,
        range: `${data.duration?.min || 0} - ${data.duration?.max || 0}`,
      },
      highDemand: data.highDemand,
      meta: data.meta,
      warehouse: {
        ...data.warehouse,
        zone,
      },
      deliverySlots: data.deliverySlots,
    };
  }

  async requestETA(): Promise<RequestETAResponse> {
    this.setIsETALoading(true);

    const { lat, lng } = this.userStore.deliveryAddress
      ? this.userStore.deliveryAddress.coordinates
      : company.config.warehouse.location;

    const etaCalculation = await this.deliveryStore.requestActualVersionETA(
      `${lat},${lng}`,
    );

    const output: Pick<RequestETAResponse, 'oldPrice' | 'newPrice'> = {
      oldPrice:
        this.etaCalculation?.cost[checkoutStore.deliveryMethod]
          ?.shippingPounds ?? '0',
      newPrice:
        etaCalculation.cost[checkoutStore.deliveryMethod]?.shippingPounds ??
        '0',
    };

    checkoutStore.setDeliverySlots(etaCalculation.deliverySlots ?? null);

    if (!etaCalculation) {
      this.setIsETALoading(false);
      return Promise.reject({});
    }

    runInAction(() => {
      if (
        this.etaCalculation?.highDemand !== etaCalculation.highDemand ||
        this.etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
          ?.shipping !==
          etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]?.shipping ||
        this.etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
          ?.threshold !==
          etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]?.threshold ||
        this.etaCalculation.duration.range !== etaCalculation.duration.range
      ) {
        analyticsService.track(AnalyticsEvent.GENERAL_ETA_CHANGED, {
          eta_min: etaCalculation.duration.min ?? 0,
          eta_max: etaCalculation.duration.max ?? 0,
          delivery_fee:
            etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
              ?.shippingPounds ?? 0,
          threshold:
            etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
              ?.thresholdPounds ?? 0,
          is_surger: etaCalculation.highDemand ?? false,
        });
      }
      this.setEtaCalculation(etaCalculation);
      checkoutStore.setDeliveryPrices(output);
      if (output.oldPrice !== output.newPrice) {
        checkoutStore.setIsUpdatedDeliveryPopover(true);
      }
      mainStore.sendToRN('sendTags', {
        warehouse_code: etaCalculation.warehouse.code,
      });
      mainStore.sendToRN('setUserProperties', {
        'Address: warehouse code': etaCalculation.warehouse.id,
      });
    });

    this.setIsETALoading(false);

    return {
      ...output,
      etaCalculation: {
        ...this.etaCalculation,
        paymentMethods: etaCalculation.paymentMethods,
        warehouse: etaCalculation.warehouse,
      } as ETACalculation,
    };
  }

  requestETASlot(): Promise<RequestETAResponse> {
    if (!this.userStore.deliveryAddress) {
      return Promise.reject();
    }
    if (company.isMarketplaceVariant) {
      return Promise.reject();
    }
    this.setIsETALoading(true);
    return ETARequests.getETASlot({
      pos: `${this.userStore.deliveryAddress.coordinates.lat},${this.userStore.deliveryAddress.coordinates.lng}`,
      seller: 'JIFFY',
      ...(checkoutStore.selectedDeliverySlot?.schedule_slot_id
        ? {
            deliverySlotId:
              checkoutStore.selectedDeliverySlot?.schedule_slot_id,
          }
        : {
            scheduleSlotId:
              checkoutStore.selectedDeliverySlot?.schedule_slot_id,
            currentDate: +(checkoutStore.selectedDeliveryDate ?? 0) / 1000,
          }),
    })
      .then(({ data }) => {
        const etaCalculation: ETACalculation =
          this.etaCalculationResponse(data);
        const output: RequestETAResponse = {
          oldPrice:
            this.etaCalculation?.cost[checkoutStore.deliveryMethod]
              ?.shippingPounds ?? '0',
          newPrice:
            etaCalculation.cost[checkoutStore.deliveryMethod]?.shippingPounds ??
            '0',
        };

        if (!etaCalculation || !Object.keys(etaCalculation.cost).length) {
          return Promise.reject({});
        }

        runInAction(() => {
          if (
            this.etaCalculation?.highDemand !== etaCalculation.highDemand ||
            this.etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
              ?.shipping !==
              etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
                ?.shipping ||
            this.etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
              ?.threshold !==
              etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
                ?.threshold ||
            this.etaCalculation.duration.range !== etaCalculation.duration.range
          ) {
            analyticsService.track(AnalyticsEvent.GENERAL_ETA_CHANGED, {
              eta_min: etaCalculation.duration.min ?? 0,
              eta_max: etaCalculation.duration.max ?? 0,
              delivery_fee:
                etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
                  ?.shippingPounds ?? 0,
              threshold:
                etaCalculation.cost[ETADeliveryMethodType.JiffyDelivery]
                  ?.thresholdPounds ?? 0,
              is_surger: etaCalculation.highDemand ?? false,
            });
          }
          this.setEtaSlotCalculation(etaCalculation);
          // checkoutStore.setDeliverySlots(data.deliverySlots || null);
          checkoutStore.setDeliveryPrices(output);
          if (output.oldPrice !== output.newPrice) {
            checkoutStore.setIsUpdatedDeliveryPopover(true);
          }
          mainStore.sendToRN('sendTags', {
            warehouse_code: data.warehouse.code,
          });
          mainStore.sendToRN('setUserProperties', {
            'Address: warehouse code': data.warehouse.id,
          });

          if (!checkoutStore.selectedDeliveryDate) {
            checkoutStore.setSelectedDate('0');
            checkoutStore.setSelectedSlot(null);
          } else if (checkoutStore.selectedDeliveryDate !== '0') {
            const selectedDay =
              checkoutStore.deliverySlots[checkoutStore.selectedDeliveryDate];

            if (selectedDay) {
              let selectedSlot = selectedDay.find(
                (slot) =>
                  slot.schedule_slot_id ===
                  checkoutStore.selectedDeliverySlot?.schedule_slot_id,
              );
              if (!selectedSlot) {
                selectedSlot = checkoutStore.selectedDeliverySlot
                  ? selectedDay.find(
                      (slot) =>
                        slot.delivery_min_time >=
                        checkoutStore.selectedDeliverySlot!.delivery_max_time,
                    )
                  : selectedDay[0];
              }
            } else {
              const defaultDay = Object.entries(
                checkoutStore.deliverySlots || {},
              ).find(
                (i) =>
                  +i[0] > +(checkoutStore.selectedDeliveryDate || 0) &&
                  i[1].length > 0,
              );
              if (defaultDay) {
                checkoutStore.setSelectedDate(defaultDay[0]);
                // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                checkoutStore.setSelectedSlot(defaultDay[1][0]);
              }
            }
          }
        });
        return Promise.resolve(output);
      })
      .catch(async (error) => {
        console.error(error);
        if (
          !error.response &&
          (error.code === 'ECONNABORTED' || error.message === 'Network Error')
        ) {
          return Promise.reject();
        }

        await this.requestETA();
        return Promise.reject();
      })
      .finally(() => this.setIsETALoading(false));
  }

  async newOrder(data: OrderData): Promise<NewOrderResponse | null> {
    if (!data.phone) {
      return null;
    }

    this.isLoading = true;
    /**
     * Code below provides bonuses info for backend
     * "paidBonuses", "promocodeDiscountAmount" are required fields for new order request,
     * and they should be presented in each cart item
     * */
    const calculatedItemsMap: Record<string, Record<string, number>> = {};
    const calculatedItems =
      cartStore.deliveriesOrders.flatMap(({ items }) => items) ?? [];
    for (
      let calculatedItemIndex = 0;
      calculatedItemIndex < calculatedItems.length;
      calculatedItemIndex += 1
    ) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      const sku = calculatedItems[calculatedItemIndex].sku;
      calculatedItemsMap[sku] = {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        paidBonuses: calculatedItems[calculatedItemIndex].paid_bonuses,
        promocodeDiscountAmount:
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          calculatedItems[calculatedItemIndex].promocode_discount_amount,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        paidPrice: calculatedItems[calculatedItemIndex].paid_price,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        discountAmount: calculatedItems[calculatedItemIndex].discount_amount,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        basePrice: calculatedItems[calculatedItemIndex].base_price,
      };
    }

    const payment_method = Object.values(MobilePaymentMethod).includes(
      checkoutStore.activePaymentMethod as MobilePaymentMethod,
    )
      ? ETAPaymentMethod.Card
      : checkoutStore.activePaymentMethod === AdditionalPaymentMethod.KNET ||
          checkoutStore.activePaymentMethod ===
            AdditionalPaymentMethod.VisaMastercard
        ? ETAPaymentMethod.Card
        : isPhonePaymentMethod(
              checkoutStore.activePaymentMethod as ETAPaymentMethod,
            )
          ? (checkoutStore.activePaymentMethod as ETAPaymentMethod)
          : checkoutStore.activePaymentMethod === ETAPaymentMethod.QR
            ? ETAPaymentMethod.QR
            : (checkoutStore.activePaymentMethod as ETAPaymentMethod);

    const payment_system = checkoutStore.activePaymentSystem;

    const payment_method_id =
      checkoutStore.activePaymentMethod === AdditionalPaymentMethod.KNET
        ? 1
        : checkoutStore.activePaymentMethod ===
            AdditionalPaymentMethod.VisaMastercard
          ? 2
          : undefined;

    const session_id =
      !payment_method_id && payment_method === ETAPaymentMethod.Card
        ? data.sessionId
        : undefined;

    try {
      if (!this.idempotencyKey) {
        this.idempotencyKey = uuidv4();
      }

      const deliveries =
        checkoutStore.deliveries?.map(
          ({ slotDeliveryDetails, itemsIds }, index) => {
            const order = cartStore.deliveriesOrders[index];

            if (!order) {
              throw new Error('Order not found');
            }

            const {
              base_total,
              discount_total,
              is_delivery_free,
              paid_bonuses,
              paid_total,
              promocode,
              promocodeError,
              promocode_discount,
              service_fee,
              should_use_loyalty_points,
              should_use_bonuses,
              delivery_info: { paid_price },
            } = order;

            const items: NewOrderCartItemOffer[] = [];

            itemsIds.forEach((id) => {
              const cartItem = cartStore.selectedCartItems.find(
                (item) => item.sku === id,
              );

              if (!cartItem) {
                return;
              }

              const deliveryItem = order.items.find(
                ({ sku }) => sku === (cartItem as Offer).sku,
              );

              if (!deliveryItem) {
                return;
              }

              const {
                base_price,
                discount_amount,
                image,
                name,
                paid_bonuses,
                paid_price,
                product_id,
                promocode_discount_amount,
                requested_quantity,
                sku,
                weight,
              } = deliveryItem;

              items.push({
                base_price,
                discount_amount,
                image,
                name,
                paid_bonuses,
                paid_price,
                promocode_discount_amount,
                requested_quantity,
                sku,
                weight,
                product_id: `${product_id}`,
                price_per_unit: cartItem.pricePerUnit,
              });
            });

            const result = {
              base_total,
              discount_total,
              is_delivery_free,
              paid_bonuses,
              paid_total,
              promocode: (!promocodeError && promocode) || null,
              promocode_discount,
              service_fee,
              should_use_loyalty_points,
              should_use_bonuses,
              mechanic: MechanicType.ON_DEMAND,
              items,
              ...(order.bundles?.length
                ? {
                    bundles: order.bundles.map((i) => ({
                      id: i.id,
                      requested_quantity: i.requested_quantity,
                    })),
                  }
                : {}),

              // FIXME: where to get? ETA slot?
              delivery_price: paid_price,
              tips_amount: 0,
            };

            if (slotDeliveryDetails) {
              return {
                ...result,
                mechanic: MechanicType.SLOT,
                slot_delivery_details: {
                  current_date: slotDeliveryDetails.currentDate,
                  schedule_slot_id: slotDeliveryDetails.scheduleSlotId,
                },
              };
            }

            return result;
          },
        ) ?? [];

      const customerCash =
        (data.hasExactAmount && deliveries[0] && deliveries[0].paid_total) ||
        (isNumber(data.customerCash) && !isNaN(data.customerCash))
          ? data.customerCash * 100
          : 0;

      Sentry.withScope((scope) => {
        scope.setExtras({
          context: 'newOrder',
          error: 'REQUEST DATA NEW ORDER',
          data: {
            deliveiesInStore: checkoutStore.deliveries,
            calculateOrders: cartStore.deliveriesOrders,
            order: {
              customer_id: this.userStore.personalData.id?.toString() ?? '',
              currency: this.currency,
              warehouse_code: this.etaWarehouseCode,
              delivery_method: data.deliveryMethod,
              client_source: 'App',
              save_card: data.saveCard,
              phone: data.mobileMoneyPhone,
              payment_method,
              payment_system,
              session_id,
              deliveries,
              customerCash,
            },
            recipient: {
              full_name: data.fullName,
              email: data.email.trim(),
              phone: data.phone,
            },
            address: {
              city: this.userStore.deliveryAddress?.city ?? '',
              country_id: this.userStore.deliveryAddress?.country ?? '',
              region: this.userStore.deliveryAddress?.region ?? '',
              address_1: data.address ?? '',
              address_2: data.address2 ?? '',
              floor: data.addressFloor ?? '',
              building: data.addressBld ?? '',
              apartment: data.addressApt ?? '',
              latitude: this.userStore.deliveryAddress?.coordinates.lat ?? null,
              longitude:
                this.userStore.deliveryAddress?.coordinates.lng ?? null,
              zip: this.userStore.deliveryAddress?.zip ?? '',
              comment: data.comment,
              instructions: data.instructions ?? null,
              notes: data.notes ?? '',
            },
          },
        });
        Sentry.captureMessage('[Checkout] PREREQUEST NEW ORDER', 'warning');
      });

      const cardId =
        (checkoutStore.activePaymentSystem === PaymentSystem.amazon ||
          checkoutStore.activePaymentSystem === PaymentSystem.adyen ||
          checkoutStore.activePaymentSystem === PaymentSystem.verifone) &&
        !checkoutStore.isAddNewCard &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
        !isPhonePaymentMethod(
          checkoutStore.activePaymentMethod as ETAPaymentMethod,
        ) &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR
          ? checkoutStore.paymentsMethodId
          : undefined;

      const flow =
        checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
        checkoutStore.activePaymentSystem === PaymentSystem.adyen
          ? { checkoutFlow: 'advanced' }
          : {};

      const deliveryInfo = this.deliveryStore.getOrderDeliveryPayload(
        data.deliveryMethod,
        deliveries.some((item) => item.mechanic === MechanicType.SLOT)
          ? MechanicType.SLOT
          : MechanicType.ON_DEMAND,
      );

      const zone =
        this.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod];

      const newOrderData = {
        order: {
          customer_id: this.userStore.personalData.id?.toString() ?? '',
          currency: this.currency,
          warehouse_code: this.etaWarehouseCode,
          zone_id: zone?.id,
          ...deliveryInfo,
          client_source: 'App',
          save_card: data.saveCard,
          session_id,
          deliveries,
          payment: {
            phone: data.mobileMoneyPhone,
            payment_method,
            payment_system,
            available_payment_providers:
              this.paymentSystems.size > 0
                ? Array.from(this.paymentSystems.keys())
                : [],
            card_id: checkoutStore.isAddNewCard
              ? undefined
              : cardId || undefined,
            pay_by_saved_card: !!cardId || undefined,
            ...data.additionalData,
            ...flow,
          },
          customerCash,
        },
        recipient: {
          full_name: data.fullName,
          email: data.email.trim(),
          phone: data.phone,
        },
        address: {
          city: this.userStore.deliveryAddress?.city ?? '',
          country_id: this.userStore.deliveryAddress?.country ?? '',
          region: this.userStore.deliveryAddress?.region ?? '',
          address_1: data.address ?? '',
          address_2: data.address2 ?? '',
          building: data.addressBld ?? '',
          floor: data.addressFloor ?? '',
          apartment: data.addressApt ?? '',
          latitude: this.userStore.deliveryAddress?.coordinates.lat ?? null,
          longitude: this.userStore.deliveryAddress?.coordinates.lng ?? null,
          zip: this.userStore.deliveryAddress?.zip ?? '',
          comment: data.comment,
          instructions: data.instructions ?? null,
          notes: data.notes ?? '',
        },
      };

      const response = await OrderRequests.newOrder(
        newOrderData,
        this.idempotencyKey,
      );

      runInAction(() => {
        this.idempotencyKey = '';
      });

      this.isLoading = false;
      return response;
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          context: 'newOrder first error',
          error: (error && (error as any)['response']) || error,
        });
        Sentry.captureMessage(
          '[Checkout] Failed create new order TOP ERROR NOT AXIOS',
          'warning',
        );
      });
      //mainStore.pushAlert('error', i18n.t('errors:unableCreateOrder'));
      if (error instanceof AxiosError) {
        await this.reCalculateDeliveries();

        Sentry.withScope((scope) => {
          scope.setExtras({
            context: 'newOrder',
            error: (error && (error as any)['response']) || error,
          });
          Sentry.captureMessage(
            '[Checkout] Failed create new order',
            'warning',
          );
        });

        const { response, status } = error;

        if (!response) {
          this.isLoading = false;
          return null;
        }

        const { data } = response;

        if (data?.data?.errorCode && handleOrderError(data)) {
          this.isLoading = false;
          throw new OrderErrorHandled();
        }

        if (data?.errors?.length) {
          if (checkoutStore.activePaymentSystem === PaymentSystem.verifone) {
            const mainError = data.errors[0];
            const errorFromArray =
              typeof mainError === 'string' ? mainError : mainError?.message;

            let errorMessage = errorFromArray || 'Payment failed';

            if (data?.data?.refusalReason) {
              errorMessage = `${errorMessage} - ${data.data.refusalReason}`;
            } else if (
              data?.data?.resultCode &&
              data.data.resultCode !== 'INVALID_DATA'
            ) {
              errorMessage = `${errorMessage} - ${data.data.resultCode}`;
            }

            errorMessage = errorMessage.replace(/\s*\(PPC\)\s*$/i, '');
            throw new Error(errorMessage);
          }
          if (checkoutStore.activePaymentSystem === PaymentSystem.adyen) {
            if (status === 500 || !data?.data?.resultCode) {
              throw new Error('Unable to create order');
            }
            const mainError = data.errors[0];
            const errorMessage =
              typeof mainError === 'string'
                ? [mainError]
                : [mainError?.message || error.message];

            if (data?.data && data?.data?.resultCode) {
              errorMessage.push(data.data.refusalReason);
            }
            throw new Error(errorMessage.filter(Boolean).join('. '));
          }
          if (checkoutStore.activePaymentSystem === PaymentSystem.pvit) {
            throw new Error(data.errors[0]);
          }
          if (
            data.errors[0] &&
            data.errors[0].toLowerCase() === 'gift out of stock'
          ) {
            mainStore.setIsShowGiftOutStockPopover(true);
            this.isLoading = false;
            return null;
          }
          if (data.errors[0] === 'Promocode is not valid') {
            runInAction(() => {
              cartStore.promocode.message =
                data.data?.payload?.err_code ===
                'DISCOUNT.COUPONS.ONLY_FIRST_ORDER_ALLOWED'
                  ? '' //i18n.t('cartPage:notFirstOrderPromocodeFreeDelivery')
                  : ''; //i18n.t('cartPage:promocodeNotFoundText');
              cartStore.promocode.coupon = null;
              cartStore.promocode.errorType = 'error';
              cartStore.promocode.success = false;
              mainStore.setIsInvalidPromocodePopover(true);
            });
            this.isLoading = false;
            return null;
          }
        }
      }
    } finally {
      this.isLoading = false;
    }
    return null;
  }

  async reCalculateDeliveries() {
    if (this.isETALoading) return;

    checkoutStore.slotsChangedEventStore.preserveDeliveryInfo(
      this.etaCalculation?.duration.range,
    );
    await this.requestETA();
    const result = checkoutStore.checkSlotExpirationOfExistingDeliveries(
      checkoutStore.sortedDeliverySlotsList,
    );
    checkoutStore.slotsChangedEventStore.checkDifferenceWithPreviousDeliveries();

    return result;
  }

  async check3dsStatus(orderId: string) {
    const res = await OrderRequests.get3dsStatus(orderId);

    this.set3dsUrl(res['3ds_url']);

    return res;
  }

  async checkOrderPayment(orderId: string): Promise<{
    address: OrderAddress | null;
    status: OrderStatus | null;
    received_bonuses: number | null;
  }> {
    try {
      const {
        order: { address, status, received_bonuses },
      } = await OrderRequests.getOrder(orderId);
      return { address, status, received_bonuses };
    } catch (error) {
      if (error instanceof AxiosError) {
        const { response } = error;

        if (!response) {
          return { address: null, status: null, received_bonuses: null };
        }
        Sentry.withScope((scope) => {
          scope.setExtras({
            context: 'checkOrderPayment',
            error: response,
          });
          Sentry.captureMessage(
            '[Checkout] Failed create new order',
            'warning',
          );
        });
      }
    }

    return { address: null, status: null, received_bonuses: null };
  }

  async orderCancel(orderId: string) {
    try {
      await OrderRequests.orderCancel(orderId);
    } catch (e) {
      console.error(e);
    }
  }

  async orderIdempotencyCancel(): Promise<boolean> {
    if (!this.idempotencyKey || !this.userStore.isAuthorized) {
      return true;
    }

    try {
      await OrderRequests.orderIdempotencyCancel({
        idempotency_key: this.idempotencyKey,
      });
      runInAction(() => {
        this.idempotencyKey = '';
      });

      return true;
    } catch (e) {
      console.error(e);
    }

    return false;
  }

  async requestOrders(): Promise<OrderShort[]> {
    if (!this.userStore.personalData.id) {
      this.orderList = null;
      return [];
    }

    this.isLoading = true;
    try {
      const { orders } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
        options: { page: 1 },
      });
      runInAction(() => {
        this.orderList = orders
          .filter((order) => order.cancel_reason !== 'CANCEL_BY_USER')
          .map((order) => this.calcOrderItems(order));
      });
      return this.orderList ?? [];
    } catch (error) {
      error && console.error(error);
    } finally {
      this.isLoading = false;
    }
    return [];
  }

  async requestPaginatedOrders(payload?: {
    page?: number;
    limit?: number;
    filters?: Record<string, string | string[]>;
  }): Promise<Nullable<{ result: OrderShort[]; hasNext: boolean }>> {
    const { page = 1, limit = 25, filters } = payload || {};

    if (!this.userStore.personalData.id) {
      return null;
    }

    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
          ...filters,
        },
        options: { page },
      });
      const totalPages = Math.ceil(total_count / limit);
      return { result: orders, hasNext: totalPages > page };
    } catch (error) {
      error && console.error(error);
    }
    return null;
  }

  async fetchOrders() {
    if (!this.userStore.personalData.id) {
      return;
    }
    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
      });
      let successfulOrders: OrdersResponse;
      if (total_count) {
        successfulOrders = await OrderRequests.getOrders({
          filter: {
            customer_id: this.userStore.personalData.id,
            status: [
              'created',
              'reserved',
              'confirmed',
              'delivery_created',
              'wh_created',
              'picking',
              'ready_to_ship',
              'in_delivery',
              'delivered',
              'delivered_partially',
              'finished',
            ],
          },
        });
      }
      runInAction(() => {
        this.orderList = orders
          .filter((order) => order.cancel_reason !== 'CANCEL_BY_USER')
          .map((order) => this.calcOrderItems(order));
        mainStore.analytics.totalSpent = mainStore.convertPenceToPounds(
          (successfulOrders?.orders || []).reduce(
            (sum, order) => sum + (order.paid_total || 0),
            0,
          ),
        );
        mainStore.analytics.totalOrders = total_count;
        mainStore.analytics.totalSuccessfulOrders =
          successfulOrders?.total_count || 0;
        if (orders.length) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          mainStore.analytics.lastOrderCreatedAt = orders[0].created_at;
          mainStore.analytics.firstOrderCreatedAt =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            orders[orders.length - 1].created_at;
        }
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total spent': mainStore.analytics.totalSpent,
        });
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total orders': total_count,
        });
        if (orders.length) {
          mainStore.sendToRN('setUserProperties', {
            'Commerce: first order date':
              mainStore.analytics.firstOrderCreatedAt,
          });
        }
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  async requestOrder(id: string): Promise<Order | OrderShort | null> {
    if (!id) {
      return null;
    }
    if (this.orderList && this.orderList.length) {
      const order = this.getOrderById(id);
      if (order && order.public_status === 'delivered' && order.has_feedback) {
        return order;
      }
    }
    try {
      const { order } = await OrderRequests.getOrder(id);
      return this.calcOrderItems(order);
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'requestOrder',
      );
    }
    return null;
  }

  async requestPayments(deliveryZoneId?: string): Promise<boolean> {
    if (!this.userStore.isAuthorized) {
      return false;
    }

    if (this.paymentSystems.size === 0) {
      return false;
    }

    try {
      const res = await OrderRequests.getPayments(
        Array.from(this.paymentSystems.keys()).filter(
          (system) => system !== PaymentSystem.cash,
        ),
        deliveryZoneId,
      );

      if (!res) {
        return false;
      }

      let cardsToSet;

      if (company.useAwsPaymentMethod) {
        cardsToSet = this.prepearAmazonPayments(res as AwsPayment[]);
      } else if (company.name === CompanyName.Heimkaup) {
        cardsToSet = this.prepearVerifonePayments(
          res.storedPaymentMethods as VerifonePaymentCard[],
        );
      } else if ('paymentMethods' in (res as object)) {
        cardsToSet = this.prepearAdyenPayments(
          (res as PaymentMethodsResponse).storedPaymentMethods,
        );
        this.paymentMethods = (res as PaymentMethodsResponse).paymentMethods;
      } else {
        cardsToSet = res;
      }

      this.setPaymentCards(cardsToSet as Payment[]);
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'requestPayments',
      );
    }
    return false;
  }

  prepearAmazonPayments(amazonCards: AwsPayment[]): Payment[] {
    return amazonCards.map((item) => ({
      card: {
        brand: '',
        checks: {
          address_line1_check: null,
          address_postal_code_check: null,
          cvc_check: null,
        },
        country: '',
        exp_month: 0,
        exp_year: 0,
        fingerprint: '',
        last4: item.public_identifier.slice(-4),
      },
      id: item.id,
      paymentToken: item.payment_system_id,
      metadata: {
        is_default: `${item.is_default}`,
      },
    }));
  }

  prepearAdyenPayments(
    payments: PaymentMethodsResponse['storedPaymentMethods'],
  ): Payment[] {
    return (
      payments?.map((payment) => ({
        id: payment.id,
        card: {
          brand: payment.brand || '',
          checks: {
            address_line1_check: null,
            address_postal_code_check: null,
            cvc_check: null,
          },
          country: '',
          exp_month: Number(payment.expiryMonth) || 0,
          exp_year: Number(payment.expiryYear) || 0,
          fingerprint: '',
          last4: payment.lastFour || '',
        },
        paymentToken: payment.storedPaymentMethodId || null,
        metadata: {
          is_default:
            'isDefault' in payment
              ? (payment as unknown as { isDefault: boolean }).isDefault
                ? 'true'
                : 'false'
              : 'false',
        },
      })) || []
    );
  }

  prepearVerifonePayments(verifoneCards: VerifonePaymentCard[]): Payment[] {
    return verifoneCards.map((card) => ({
      id: card.id,
      card: {
        brand: card.card_brand
          ? card.card_brand.toLocaleLowerCase().replace(/\s/g, '')
          : '',
        checks: {
          address_line1_check: null,
          address_postal_code_check: null,
          cvc_check: null,
        },
        country: card.metadata?.token_details?.issuer_country || '',
        exp_month: card.card_exp_month || 0,
        exp_year: card.card_exp_year || 0,
        fingerprint: card.stored_credential_reference || '',
        last4: card.card_last_four || '',
      },
      paymentToken: card.provider_token || null,
      metadata: {
        is_default: card.is_default || card.isDefault ? 'true' : 'false',
      },
    }));
  }

  createIntent(): Promise<Payment | AxiosError> {
    return OrderRequests.createIntent().catch((e) =>
      this.errorHandler(e, 'createIntent'),
    );
  }

  addPayment(id: string): Promise<Payment | AxiosError> {
    return OrderRequests.addPayment({ payment_method_id: id }).catch((e) =>
      this.errorHandler(e, 'addPayment'),
    );
  }

  deletePayment(
    id: string,
    deliveryZoneId?: string,
  ): Promise<Payment | AxiosError> {
    return OrderRequests.deletePayment({
      payment_method_id: id,
      deliveryZoneId,
    }).catch((e) => this.errorHandler(e, 'deletePayment'));
  }

  setDefaultPayment(
    id: string,
    deliveryZoneId?: string,
  ): Promise<Payment | AxiosError> {
    return OrderRequests.setDefaultPayment({
      payment_method_id: id,
      deliveryZoneId,
    }).catch((e) => this.errorHandler(e, 'setDefaultPayment'));
  }

  async addFeedback(orderId: string, data: RateInfo) {
    const ratingRate = data.rating;

    if (!ratingRate) {
      return;
    }

    const feedBackID = await this.newFeedback(orderId, ratingRate);

    if (feedBackID && Object.keys(data).length > 1) {
      return this.fillFeedback(orderId, feedBackID, {
        rating: ratingRate,
        delivery_rating: data.deliveryRating ?? -1,
        product_quality_rating: data.productQualityRating ?? -1,
        product_variety_rating: data.productVarietyRating ?? -1,
        comment: data.comment ?? '',
      });
    }

    return true;
  }

  async newFeedback(orderId: string, rating: number): Promise<string> {
    try {
      const { id } = await OrderRequests.newFeedback({
        rating,
        order_id: orderId,
      });

      analyticsService.track(AnalyticsEvent.RATE_ORDER_GENERAL, {
        order_id: orderId,
        score: rating,
      });

      analyticsService.track(AnalyticsEvent.RATE_ORDER_GENERAL_FIREBASE, {
        order_id: orderId,
        score: rating,
      });

      return id;
    } catch (e) {
      await this.errorHandler(e as AxiosError<ApiErrorResponse>, 'newFeedback');
    }
    return '';
  }

  async fillFeedback(
    orderId: string,
    feedbackId: string,
    data: FillFeedbackRequest,
  ): Promise<boolean> {
    const {
      delivery_rating,
      product_variety_rating,
      product_quality_rating,
      comment,
      rating,
    } = data;

    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order rating': rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order delivery rating': delivery_rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order product variety rating': product_variety_rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order product quality rating': product_quality_rating,
    });
    analyticsService.track(AnalyticsEvent.RATE_ORDER_DETAILED, {
      order_id: orderId,
      delivery: delivery_rating,
      product_variety: product_variety_rating,
      product_quality: product_quality_rating,
      comment_provided: !!comment.length,
    });
    analyticsService.track(AnalyticsEvent.RATE_ORDER_DETAILED_FIREBASE, {
      order_id: orderId,
      delivery: delivery_rating,
      product_variety: product_variety_rating,
      product_quality: product_quality_rating,
      comment_provided: !!comment.length,
    });

    try {
      await OrderRequests.fillFeedback(feedbackId, data);

      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fillFeedback',
      );
    }

    return false;
  }

  async fetchGifts(): Promise<boolean> {
    try {
      const { data } = await OrderRequests.getGifts(
        this.etaCalculation?.warehouse.code || '',
      );
      if (!data) {
        this.setGift(null);
        if (this.activeGift) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
        return true;
      }
      const isMinimumOrderAmountDiff = !!(
        this.gift &&
        this.gift.minimum_order_amount !== data.minimum_order_amount
      );
      this.setGift(data);
      this.setIsAuthInCart(false);
      if (this.activeGift) {
        if (
          (isMinimumOrderAmountDiff &&
            mainStore.convertPoundsToPence(cartStore.finalPrice) <
              mainStore.convertPoundsToPence(data.minimum_order_amount)) ||
          !data.gift_items.some((item) => item.id === this.activeGift?.id)
        ) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const { response } = error;
        if (!response) {
          return false;
        }
        if (
          this.isAuthInCart &&
          response.data?.errors?.length &&
          response.data.errors[0] &&
          response.data.errors[0].toLowerCase() === 'gift already received'
        ) {
          mainStore.setIsShowGiftAlreadyReceivedPopover(true);
        }
        this.setGift(null);
        if (this.activeGift) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
      }
    }
    return true;
  }

  async payTip(
    orderId: string,
    payAmount: string,
    isSaveCard: boolean,
  ): Promise<string> {
    try {
      const { client_secret } = await OrderRequests.payTip(orderId, {
        amount: mainStore.convertPoundsToPence(payAmount),
        save_card: isSaveCard,
        currency: this.currency,
        email: this.userStore.personalData.email || '',
      });
      return client_secret || '';
    } catch (error) {
      error && console.error(error);
    }
    return '';
  }

  async checkPayedTip(orderId: string): Promise<boolean> {
    if (!this.userStore.isAuthorized) {
      return true;
    }
    try {
      const { status } = await OrderRequests.checkPayedTip(orderId);
      return status === 'received';
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          return false;
        }
        error && console.error(error);
      }
    }
    return true;
  }

  async checkIsFirstOrder(isForce?: boolean) {
    if (this.isFirstOrder !== null && !isForce) {
      return;
    }

    if (!this.userStore.personalData.id) {
      this.setIsFirstOrder(true);

      return;
    }

    try {
      // gets all statuses exclude canceled, delivery_failed, because such statuses are ignored on backend
      const { total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id ?? '',
          status: [
            'created',
            'reserved',
            'confirmed',
            'delivery_created',
            'wh_created',
            'picking',
            'ready_to_ship',
            'in_delivery',
            'delivered',
            'delivered_partially',
            'finished',
          ],
        },
        options: {
          page: 1,
          limit: 1,
        },
      });

      this.setIsFirstOrder(total_count === 0);
      return total_count === 0;
    } catch {
      this.setIsFirstOrder(true);
    }
  }

  requestCurrency() {
    OrderRequests.getCurrency()
      .then(({ data: { currency = '', digits_after_coma = 2 } }) => {
        this.setCurrency(currency);
        mainStore.setDigitsAfterComma(digits_after_coma);
      })
      .catch((e) => this.errorHandler(e, 'requestCurrency'));
  }

  async requestPaymentSystems(deliveryZoneId?: string): Promise<void> {
    if (!this.userStore.isAuthorized) {
      return;
    }

    try {
      const systems = await OrderRequests.getPaymentSystems(deliveryZoneId);

      this.setPaymentSystems(systems);
    } catch (e) {
      this.setPaymentSystems([]);
      this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'paymentSystems',
      ).catch(() => void 0);
    }
  }

  // Errors
  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<AxiosError> => mainStore.errorHandler(error, context);

  clearOrderList() {
    this.orderList = [];
  }

  private _clearOrdersPollingPayload() {
    if (this._ordersPollingTimeoutRef) {
      clearTimeout(this._ordersPollingTimeoutRef);
      this._ordersPollingTimeoutRef = null;
    }
  }
  private _startOrdersPolling(opts?: { timeout?: number }) {
    const { timeout = 30 * SECOND } = opts || {};

    this._clearOrdersPollingPayload();

    const handler = async () => {
      const orders = await this.requestOrders();

      const onlyActiveOrders =
        orders.filter((order) =>
          ACTIVE_ORDER_STATUS_CRITERIA.includes(order.public_status),
        ) || [];

      if (onlyActiveOrders.length === 0) {
        this.setOrdersPollingEnabled(false);
        return;
      }

      this._ordersPollingTimeoutRef = setTimeout(handler, timeout);
    };

    handler();
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}
